import React, { useState } from 'react';
import { ChipSelect, ReadOnlyValue } from '../../../components/ui';
import { useReadOnly } from '../../../contexts/WizardAccessContext';
import { ContextQuestion } from './useContextStep';

/**
 * One numbered context question from the 3a mock: ai-chip number tile
 * (Robbie is asking), question + why-it-matters line, then the answer chips
 * Robbie proposed — plus an "Other" chip, because a large share of these specs
 * are ranges a buyer states in their own words ("200-240 V", "-20 to +85 C")
 * and no chip list can hold those. A viewer reads the answer that was given,
 * not a control for giving one.
 *
 * The answer is always ONE string, chips and Other text joined by ", ". That is
 * deliberate: everything downstream (completion counting, validation, and the
 * resolver agent that finally reads it) already speaks plain strings, so
 * multi-select costs nothing on the way back.
 */
export interface ContextQuestionCardProps {
  index: number;
  question: ContextQuestion;
  value: any;
  onChange: (value: any) => void;
  isLast?: boolean;
  /**
   * Overrides the wizard-access verdict when the hosting panel computed an
   * effective flag — the pending end user is a VIEWER in the role model, yet
   * these questions are exactly what was handed to them to answer.
   */
  readOnly?: boolean;
}

/** Sentinel chip value; kept out of the answer string. */
const OTHER = '__other__';

const helperFor = (q: ContextQuestion): string | null => {
  if (q.helpText) return q.helpText;
  if (q.isRobotCategoryConfirmation && q.detectedValue)
    return 'I detected this from your BOM — confirm or correct me.';
  if (q.componentTypes && q.componentTypes.length > 0)
    return `Applies to ${q.componentTypes.join(', ')}.`;
  return null;
};

/**
 * Split a stored answer back into picked chips and free text.
 *
 * An option may itself contain a comma ("200 V AC, 3-phase"), so an exact match
 * on the whole answer wins before anything is split.
 */
const splitAnswer = (value: any, options: string[]): { chips: string[]; other: string } => {
  const raw = value === undefined || value === null ? '' : String(value);
  if (!raw.trim()) return { chips: [], other: '' };
  if (options.includes(raw.trim())) return { chips: [raw.trim()], other: '' };

  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  return {
    chips: parts.filter((p) => options.includes(p)),
    other: parts.filter((p) => !options.includes(p)).join(', '),
  };
};

/** The bordered text-input frame shared with the BOM-answers list. */
export const inputShell =
  'flex max-w-[280px] items-center gap-s3 rounded-control border border-line-strong bg-surface ' +
  'px-[12px] py-[8px] transition-colors duration-100 focus-within:border-action focus-within:ring-1 focus-within:ring-action-border';

export const ContextQuestionCard: React.FC<ContextQuestionCardProps> = ({
  index,
  question,
  value,
  onChange,
  isLast = false,
  readOnly: readOnlyProp,
}) => {
  const viewerReadOnly = useReadOnly();
  const readOnly = readOnlyProp ?? viewerReadOnly;
  const helper = helperFor(question);
  const answerText = value === undefined || value === null || value === ''
    ? undefined
    : `${value}${question.unit ? ` ${question.unit}` : ''}`;
  // Curated templates sometimes end their list with a literal "Other" chip that
  // selects the word and nothing more. Drop it — this card appends its own, and
  // that one opens a box the buyer can actually type into.
  const options = (question.options || []).filter((o) => o.trim().toLowerCase() !== 'other');
  const hasOptions = options.length > 0;
  const multi = question.answerType === 'multi_select';
  const { chips, other } = splitAnswer(value, options);

  // Once the buyer opens Other the box stays open, even while it is empty.
  // Deriving it from `other` alone would snap the box shut the moment they
  // cleared what they had typed, mid-edit.
  const [otherOpen, setOtherOpen] = useState(false);
  const showOther = otherOpen || other !== '';

  const commit = (nextChips: string[], nextOther: string) =>
    onChange([...nextChips, nextOther.trim()].filter(Boolean).join(', '));

  // ChipSelect already enforces one-of in single mode, so a real chip and Other
  // deselect each other for free; multi mode stacks them.
  const handleChips = (next: string[]) => {
    const wantsOther = next.includes(OTHER);
    setOtherOpen(wantsOther);
    commit(next.filter((v) => v !== OTHER), wantsOther ? other : '');
  };

  const placeholder = question.placeholder || `e.g. ${question.unit ? '16' : '…'}`;

  return (
    <div className={`px-s6 py-[16px] ${isLast ? '' : 'border-b border-line-divider'}`}>
      <div className="mb-s4 flex items-start gap-s4">
        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[7px] border border-robbie-border bg-ai-chip text-[11px] font-bold text-robbie-ink">
          {index}
        </span>
        <div className="min-w-0">
          <label htmlFor={`context-${question.field}`} className="block text-row-title text-ink">
            {question.question}
          </label>
          {helper && <p className="m-0 mt-[2px] text-meta text-ink-muted">{helper}</p>}
        </div>
      </div>

      <div className="pl-[32px]">
        {readOnly ? (
          <ReadOnlyValue value={answerText} emptyLabel="Not answered" />
        ) : hasOptions ? (
          <div className="flex flex-col gap-s3">
            <ChipSelect
              mode={multi ? 'multi' : 'single'}
              check
              options={[
                ...options.map((o) => ({ value: o, label: o })),
                { value: OTHER, label: 'Other' },
              ]}
              value={showOther ? [...chips, OTHER] : chips}
              onChange={handleChips}
            />
            {showOther && (
              <div className={inputShell}>
                <input
                  id={`context-${question.field}`}
                  type="text"
                  autoFocus
                  value={other}
                  onChange={(e) => commit(chips, e.target.value)}
                  placeholder={multi ? 'Anything else — a range is fine' : 'Type a value or range'}
                  className="w-full border-none bg-transparent p-0 font-sans text-body-md text-ink outline-none placeholder:text-ink-muted"
                />
                {question.unit && (
                  <span className="shrink-0 select-none text-body-sm font-medium text-ink-muted">
                    {question.unit}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={inputShell}>
            <input
              id={`context-${question.field}`}
              type={question.inputType || 'text'}
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="w-full border-none bg-transparent p-0 font-sans text-body-md text-ink outline-none placeholder:text-ink-muted"
            />
            {question.unit && (
              <span className="shrink-0 select-none text-body-sm font-medium text-ink-muted">{question.unit}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContextQuestionCard;
